import styled from '@emotion/styled';
import type { RadioSize, RadioVariant } from './types';

type ControlProps = {
  $size: RadioSize;
  $checked?: boolean;
};

type RootProps = {
  $variant: RadioVariant;
  $checked?: boolean;
  $disabled?: boolean;
};

export const Root = styled.label<RootProps>`
  display: inline-flex;
  align-items: center;
  justify-content: ${({ $variant }) =>
    $variant === 'card' ? 'space-between' : 'flex-start'};
  gap: ${({ theme }) => theme?.space?.sm ?? '0.5rem'};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled, theme }) =>
    $disabled ? theme?.component?.radio?.disabledOpacity ?? 0.5 : 1};
  box-sizing: border-box;
  user-select: none;

  ${({ theme, $variant, $checked }) =>
    $variant === 'card'
      ? `
    width: 100%;
    padding: ${theme?.component?.radio?.card?.padding ?? '1rem'};
    background: ${theme?.component?.radio?.card?.backgroundColor ?? '#ffffff'};
    border: 1px solid ${
      $checked
        ? theme?.component?.radio?.card?.selectedBorderColor ?? '#292929'
        : theme?.component?.radio?.card?.borderColor ?? '#e5e5e5'
    };
    border-radius: ${theme?.component?.radio?.card?.borderRadius ?? '8px'};
    transition: border-color 0.15s ease;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-color: ${
          theme?.component?.radio?.card?.hoverBorderColor ?? '#292929'
        };
      }
    }
  `
      : ''}
`;

export const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

export const Circle = styled.span<ControlProps>`
  position: relative;
  flex-shrink: 0;
  box-sizing: border-box;
  width: ${({ theme, $size }) =>
    theme?.component?.radio?.size?.[$size]?.width ?? '18px'};
  height: ${({ theme, $size }) =>
    theme?.component?.radio?.size?.[$size]?.height ?? '18px'};
  border-radius: 50%;
  border: 2px solid
    ${({ theme, $checked }) =>
      $checked
        ? theme?.component?.radio?.checkedBorderColor ?? '#292929'
        : theme?.component?.radio?.borderColor ?? '#e5e5e5'};
  background: transparent;
  transition: all 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${({ theme, $size }) =>
      theme?.component?.radio?.size?.[$size]?.inner ?? '10px'};
    height: ${({ theme, $size }) =>
      theme?.component?.radio?.size?.[$size]?.inner ?? '10px'};
    border-radius: 50%;
    background: ${({ theme, $checked }) =>
      $checked
        ? theme?.component?.radio?.checkedFill ?? '#292929'
        : 'transparent'};
    transition: background 0.15s ease;
  }
`;

export const LabelText = styled.span`
  font-size: ${({ theme }) => theme?.fontSizes?.[2] ?? 14}px;
  color: ${({ theme }) => theme?.colors?.text?.primary ?? '#292929'};
  line-height: 1.4;
  flex: 1;
`;
